import { all, call, select, takeLatest } from 'redux-saga/effects'

import { getJobForApplication, getLoopValue } from '@/jobapplication/store/selectors'
import { newSearch } from '@/results/store/actions'
import { appendQueryParams, getUrlOrigin } from '@/utils/url'

import { getCompanyLoading, getCompanyInfo } from '../../companyProfile/store/selectors'
import { NAV_BRAND } from '../../config'
import { PORTAL_TYPE_YF } from '../../config/constants'
import { getJobInfo, getJobLoading } from '../../jobDetail/store/selectors'
import { isLandingPageVisible } from '../../landingPage/store/selectors'
import { COMPANY, JOB, OPENING_TYPE } from '../../search/constants'
import { SearchTypeActions } from '../../search/store/actions'
import {
  getAutoSelectItem,
  getReducedSearchState,
  getSearchType,
  getSelectedId
} from '../../search/store/selectors'
import {
  prepareJobInfo,
  prepareCompanyInfo,
  trackOnContentLoaded,
  trackInitialSearch,
  trackManualSearch,
  trackUserInteraction
} from '../index'

import * as actions from './actions'

export function * executeTrackInitialSearch ({ payload, meta }) {
  yield call(trackInitialSearch, payload, meta)

  // Track only initial search URL. Initial job/company/LP URLs will be tracked within components
  const mainContentIsEmpty = yield * isMainContentEmpty()
  if (!mainContentIsEmpty) {
    return
  }
  // not selected job/company and not LP mean that a page contains search results
  yield call(trackOnContentLoaded)
}

export function * executeTrackManualSearch ({ payload, meta }) {
  yield call(trackManualSearch, payload, meta)
}

export function * executeTrackNumResults ({ payload, meta }) {
  const state = yield select(getReducedSearchState)
  yield call(trackUserInteraction, {
    ...meta,
    label: 'num_results',
    category: 'internal_search',
    action: '/manuelle_suche/?' + new URLSearchParams(state),
    value: payload
  })
}

export function * executeTrackOnContentLoaded ({ payload = {} } = {}) {
  let { pageType = null, selectedId = null, sourceJobId = null } = payload
  const showLandingPage = yield select(isLandingPageVisible)
  if (showLandingPage) {
    yield call(trackOnContentLoaded)
    return
  }

  const { isJobLoading, isCompanyLoading } = yield all({
    isJobLoading: select(getJobLoading),
    isCompanyLoading: select(getCompanyLoading)
  })

  if (!pageType) {
    pageType = yield select(getSearchType)
  }

  if (!selectedId) {
    selectedId = yield select(getSelectedId)
  }
  if ((pageType === JOB && isJobLoading) || (pageType === COMPANY && isCompanyLoading)) {
    return
  }

  const jobForApplication = yield select(getJobForApplication)
  const loop = yield select(getLoopValue)
  const autoSelectItem = yield select(getAutoSelectItem)
  const isNimm2Feature = !!jobForApplication?.id
  const trackingParams = { type: pageType }

  if (selectedId) {
    if (pageType === JOB) {
      const job = yield select(getJobInfo)
      const info = prepareJobInfo(isNimm2Feature ? jobForApplication : job, {
        jobOpening: autoSelectItem && !isNimm2Feature ? OPENING_TYPE.AUTO : OPENING_TYPE.CLICK,
        isNimm2Feature,
        loop: isNimm2Feature ? loop : null
      })
      // In case of selected similar job use it's url as page param
      if (sourceJobId && sourceJobId !== selectedId && !isNimm2Feature) {
        // with appended source-job-id=
        info.page = appendQueryParams(job.url, { 'source-job-id': sourceJobId })
        // and be origin relative
        info.page = info.page.substr(getUrlOrigin(info.page).length)
      } else if (isNimm2Feature) {
        info.page = jobForApplication.url
      }
      trackingParams.jobUrl = job.url
      trackingParams.info = info
    } else if (pageType === COMPANY) {
      const company = yield select(getCompanyInfo)
      trackingParams.info = prepareCompanyInfo(company, {
        profileOpening: autoSelectItem ? OPENING_TYPE.AUTO : OPENING_TYPE.CLICK,
        trackKununuListRating: NAV_BRAND.portal_type === PORTAL_TYPE_YF
      })
    }
  }
  trackingParams.id = isNimm2Feature ? jobForApplication.id : selectedId
  yield call(trackOnContentLoaded, trackingParams)
}

export function * executeTrackOnSearchChanged () {
  const mainContentIsEmpty = yield * isMainContentEmpty()
  // avoid tracking detail/landing pages twice in case setSelectedId was dispatched
  if (!mainContentIsEmpty) {
    return
  }
  yield call(trackOnContentLoaded)
}

function * isMainContentEmpty () {
  const { selectedId, showLandingPage } = yield all({
    selectedId: select(getSelectedId),
    showLandingPage: select(isLandingPageVisible)
  })
  return !selectedId && !showLandingPage
}

export default all([
  takeLatest(actions.trackInitialSearch, executeTrackInitialSearch),
  takeLatest(actions.trackManualSearch, executeTrackManualSearch),
  takeLatest(actions.trackNumResults, executeTrackNumResults),
  takeLatest(actions.trackContentLoaded, executeTrackOnContentLoaded),
  takeLatest(newSearch.success, executeTrackOnSearchChanged),
  takeLatest([...SearchTypeActions.map(actions => actions.setSelectedId)], executeTrackOnSearchChanged)
])
